<template>
  <span
    class="base-badge"
    :class="{
      'base-badge--pill': pill,
      'base-badge--rounded': rounded,
      'base-badge--outlined': outlined,
    }"
    :style="styles"
    @click="emit('click')"
  >
    <slot>
      {{ value }}
    </slot>
  </span>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  value?: string | number;
  size?: number | string;
  filled?: boolean | string;
  pill?: boolean;
  small?: boolean;
  xSmall?: boolean;
  large?: boolean;
  paddingLarge?: boolean;
  rounded?: boolean;
  outlined?: boolean;
}>(), {
  value: undefined,
  size: undefined,
  filled: undefined,
  rounded: true,
  outlined: false,
});

const emit = defineEmits(['click']);

const styles = computed(() => {
  let filled = '';
  if (typeof props.filled === 'boolean') {
    filled = 'var(--badge-color)';
  } else if (typeof props.filled === 'string') {
    filled = props.filled.includes('#') ? props.filled : `var(--${props.filled})`;
  }

  return {
    '--base-badge--filled-color': filled,
  };
});

const badgeSize = computed(() => {
  if (props.size) {
    return props.size;
  }
  if (props.xSmall) {
    return 14;
  }
  if (props.small) {
    return 20;
  }
  if (props.large) {
    return 48;
  }
  return 34;
});

const badgeFontSize = computed(() => {
  if (props.size) {
    const sizeNum = Number(props.size);
    return Math.round(sizeNum * 0.4);
  }
  if (props.xSmall) {
    return 10;
  }
  if (props.small) {
    return 12;
  }
  if (props.large) {
    return 16;
  }
  return 14;
});

const badgePadding = computed(() => {
  if (props.paddingLarge) {
    return 1.25;
  }
  return 0.25;
});

const badgeStyle = computed(() => {
  const sizeNum = Number(badgeSize.value);
  const hasUnit = Number.isNaN(sizeNum);

  if (hasUnit) {
    return {
      width: String(badgeSize.value),
      height: String(badgeSize.value),
      fontSize: `${badgeFontSize.value / 16}rem`,
      padding: `${badgePadding.value}em`,
    };
  }

  return {
    width: `${sizeNum / 16}rem`,
    height: `${sizeNum / 16}rem`,
    fontSize: `${badgeFontSize.value / 16}rem`,
    padding: `${badgePadding.value}em`,
  };
});

defineExpose({
  styles,
  badgeSize,
  badgeFontSize,
  badgePadding,
  badgeStyle,
});
</script>

<style lang="scss" scoped>
.base-badge {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: v-bind('badgeStyle.height');
  padding: calc(0.25em - 2px) calc(1em - 2px);
  border: 2px solid var(--base-badge--filled-color);
  font-size: v-bind('badgeStyle.fontSize');
  font-weight: 600;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  border-radius: 4px;
  color: #fff;
  background-color: var(--base-badge--filled-color);

  &--rounded {
    border-radius: 999px;
  }

  &--outlined {
    color: var(--base-badge--filled-color);
    background-color: unset;
  }

  &--pill {
    width: v-bind('badgeStyle.width');
    padding: v-bind('badgeStyle.padding');
  }
}
</style>
